import { users as mockUsers } from '@/mocks/users';
import type { User } from '@/domain/types';
import { requireSupabase } from '@/lib/supabase';

export const users: User[] = mockUsers;

export function getCustomers() {
  return users.filter((user) => user.role === 'customer');
}

export function getUserById(id: string) {
  return users.find((user) => user.id === id);
}

export interface AdminCustomer {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  orderCount: number;
  licenseCount: number;
  createdAt: string;
}

export interface AdminCustomerOrder {
  id: string;
  orderRef: string;
  productName: string;
  amount: number;
  currency: string;
  status: string;
  createdAt: string;
}

export interface AdminCustomerLicense {
  id: string;
  productName: string;
  licenseKeyLast4: string;
  status: string;
  expiresAt: string | null;
}

type CustomerRow = {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  created_at: string;
};

export async function listAdminCustomers(): Promise<AdminCustomer[]> {
  const supabase = requireSupabase();

  const { data, error } = await supabase
    .from('profiles')
    .select('id, email, name, role, status, created_at')
    .order('created_at', { ascending: false });

  if (error) throw error;
  const rows = (data ?? []) as CustomerRow[];
  if (rows.length === 0) return [];

  const ids = rows.map((r) => r.id);

  const [orderResult, licenseResult] = await Promise.all([
    supabase.from('orders').select('user_id').in('user_id', ids),
    supabase.from('licenses').select('user_id').in('user_id', ids),
  ]);

  const orderCounts: Record<string, number> = {};
  const licenseCounts: Record<string, number> = {};
  (orderResult.data ?? []).forEach((r: any) => { orderCounts[r.user_id] = (orderCounts[r.user_id] ?? 0) + 1; });
  (licenseResult.data ?? []).forEach((r: any) => { licenseCounts[r.user_id] = (licenseCounts[r.user_id] ?? 0) + 1; });

  return rows.map((row) => ({
    id: row.id,
    email: row.email,
    name: row.name,
    role: row.role,
    status: row.status,
    orderCount: orderCounts[row.id] ?? 0,
    licenseCount: licenseCounts[row.id] ?? 0,
    createdAt: row.created_at,
  }));
}

export async function getAdminCustomerOrders(userId: string): Promise<AdminCustomerOrder[]> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('orders')
    .select('*, products!product_id(name)')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(10);

  if (error) throw error;
  return (data ?? []).map((row: any) => ({
    id: row.id,
    orderRef: row.order_ref,
    productName: row.products?.name || row.product_id,
    amount: Number(row.amount),
    currency: row.currency,
    status: row.status,
    createdAt: row.created_at,
  }));
}

export async function getAdminCustomerLicenses(userId: string): Promise<AdminCustomerLicense[]> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('licenses')
    .select('*, products!product_id(name)')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(10);

  if (error) throw error;
  return (data ?? []).map((row: any) => ({
    id: row.id,
    productName: row.products?.name || row.product_id,
    licenseKeyLast4: row.license_key_last4,
    status: row.status,
    expiresAt: row.expires_at,
  }));
}

export async function updateUserProfile(data: { name: string; avatarUrl?: string | null }): Promise<void> {
  const supabase = requireSupabase();
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (userError || !user) throw new Error('Not authenticated.');
  const { error } = await supabase
    .from('profiles')
    .update({ name: data.name, avatar_url: data.avatarUrl ?? null, updated_at: new Date().toISOString() })
    .eq('id', user.id);
  if (error) throw error;
}

export async function deactivateAccount(): Promise<void> {
  const supabase = requireSupabase();
  const { error } = await supabase.rpc('customer_deactivate_account');
  if (error) throw error;
}

export async function checkAccountStatus(): Promise<'active' | 'reactivated' | 'expired'> {
  const supabase = requireSupabase();
  const { data, error } = await supabase.rpc('check_account_status');
  if (error) throw error;
  return data as 'active' | 'reactivated' | 'expired';
}

export async function updateUserPassword(currentPassword: string, newPassword: string): Promise<void> {
  const supabase = requireSupabase();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user?.email) throw new Error('Not authenticated.');
  // Verify current password
  const { error: verifyError } = await supabase.auth.signInWithPassword({
    email: user.email,
    password: currentPassword,
  });
  if (verifyError) throw new Error('Current password is incorrect.');
  // Update password
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
}
